<template>
    <section class="bg-white">
      <div class="relative z-20 max-w-5xl mx-auto lg:px-4">
        <!-- Main Image -->
        <nuxt-img
          :src="mainImageSrc"
          :alt="mainImageAlt"
          sizes="xs:330px sm:440px md:760px lg:1024px xl:990px 2xl:990px"
          loading="lazy"
          format="webp"
          width="990"
          height="480"
          class="mx-auto"
        />
  
        <!-- Features Section -->
        <div class="px-4 py-8 bg-black md:px-8 md:py-16 lg:px-0">
          <div class="max-w-3xl mx-auto">
            <h1 class="text-xl font-bold text-center text-white sm:text-xl md:text-2xl xl:text-3xl lg:text-left" v-html="featuresHeading"></h1>
            <div class="mt-6 text-center text-white sm:text-lg xl:text-xl font-ww-light lg:text-left" v-html="featuresDescription"></div>
  
            <!-- Features Grid -->
            <div class="grid grid-cols-12 mt-16 sm:gap-x-8 lg:gap-x-16 gap-y-12">
              <div
                v-for="(feature, index) in features"
                :key="index"
                class="flex flex-col col-span-12 lg:col-span-4 sm:col-span-6 gap-y-3"
              >
                <nuxt-img
                  :src="feature.imgSrc"
                  :alt="feature.alt"
                  sizes="xs:33px sm:33px md:33px lg:1024px xl:33px 2xl:33px"
                  loading="lazy"
                  format="webp"
                  width="33"
                  height="44"
                  class="mx-auto lg:mx-0"
                />
                <div class="text-base text-center text-white sm:text-lg xl:text-xl lg:text-left">{{ feature.text }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      <!-- Background Section -->
      <div class="relative bg-lifeboost-black-200 lg:-mt-32">
        <nuxt-img
          :src="backgroundImageSrc"
          :alt="backgroundImageAlt"
          class="absolute object-cover object-top w-full h-full xl:opacity-100 opacity-40"
          sizes="xs:330px sm:440px md:760px lg:1024px xl:1200px 2xl:1400px"
          loading="lazy"
          format="webp"
          width="1440"
          height="893"
        />
        <div class="relative z-10 grid max-w-5xl grid-cols-12 py-8 mx-auto lg:py-32 lg:px-4">
        
          <div class="max-w-3xl col-span-12 px-6 mx-auto md:px-4 lg:pt-24 lg:col-span-12 lg:px-4">
            <div class="text-xl font-bold text-center text-white sm:text-xl md:text-2xl xl:text-28 lg:text-left" v-html="backgroundHeading"></div>
            <div class="mt-6 text-center text-white sm:text-lg xl:text-xl font-ww-light lg:text-left" v-html="backgroundDescription"></div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  // Props for dynamic content
  const props = defineProps({
    mainImageSrc: {
      type: String,
      default: '/assets/images/coffee-with-logo.png',
    },
    mainImageAlt: {
      type: String,
      default: 'Coffee with logo',
    },
    featuresHeading: {
      type: String,
      default: "The World's Premium Low-Acid, Healthy Coffee, Now with Price Protection",
    },
    featuresDescription: {
      type: String,
      default: `While other coffee companies are cutting corners to combat rising costs, we maintain our uncompromising standards:`,
    },
    features: {
      type: Array,
      default: () => [
        {
          imgSrc: '/assets/images/icon-news-1.png',
          alt: 'Single Origin',
          text: 'Single Origin, High-Altitude Grown (5,700+ ft)',
        },
        {
          imgSrc: '/assets/images/icon-news-2.png',
          alt: '3rd Party Tested',
          text: 'Molds, heavy metals plus 450+ toxins and pesticides" to "Molds, Heavy Metals Plus 450+ Toxins and Pesticides',
        },
        {
          imgSrc: '/assets/images/icon-news-3.png',
          alt: 'Low Acid',
          text: 'Stomach-Friendly Low Acid',
        },
        {
          imgSrc: '/assets/images/icon-news-4.png',
          alt: 'USDA Organic Certified',
          text: 'USDA Organic Certified',
        },
        {
          imgSrc: '/assets/images/icon-news-5.png',
          alt: 'Hand-Selected',
          text: 'Hand-Selected & Small Batch Roasted',
        },
        {
          imgSrc: '/assets/images/icon-news-6.png',
          alt: 'Shade Grown',
          text: 'Shade Grown & Spring Water Washed',
        },
        {
          imgSrc: '/assets/images/icon-news-7.png',
          alt: 'TRUSTPURE Process',
          text: 'TRUSTPURE process',
        },
      ],
    },
    backgroundImageSrc: {
      type: String,
      default: '/assets/images/bg-mountain.png',
    },
    backgroundImageAlt: {
      type: String,
      default: 'Mountain background',
    },
    backgroundHeading: {
      type: String,
      default: 'From Mountain to Morning Cup',
    },
    backgroundDescription: {
      type: String,
      default: `Our journey begins in the pristine mountains of Central America, where our coffee cherries are grown at elevations above 5,700 feet in rich, volcanic soil under a canopy of guava trees.
        <br><br>This painstaking process results in coffee that's:<br><br>
        • Naturally Low Acid (pH tested)<br>
        • Free from Harmful Mycotoxins<br>
        • Tested for 450+ different Pesticides, Chemicals, Heavy Metals, Glyphosate<br>
        • Easier on Your Stomach<br>
        • Clean, Smooth Taste<br>
        • Rich in Antioxidants<br>
        • Healthy coffee that actually tastes good`,
    },
  });
  
  defineBuilderComponent({
    friendlyName: 'FeatureWithImageAndIcons',
    inputs: [
      {
        name: 'mainImageSrc',
        type: 'file',
        friendlyName: 'Main Image Source',
        allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
        defaultValue: '/assets/images/coffee-with-logo.png',
      },
      {
        name: 'mainImageAlt',
        type: 'text',
        friendlyName: 'Main Image Alt Text',
        defaultValue: 'Coffee with logo',
      },
      {
        name: 'featuresHeading',
        type: 'richText',
        friendlyName: 'Features Heading',
        defaultValue: "The World's Premium Low-Acid, Healthy Coffee, Now with Price Protection",
      },
      {
        name: 'featuresDescription',
        type: 'richText',
        friendlyName: 'Features Description',
        defaultValue: `While other coffee companies are cutting corners to combat rising costs, we maintain our uncompromising standards:`,
      },
      {
        name: 'features',
        type: 'list',
        friendlyName: 'Features List',
        subFields: [
          {
            name: 'imgSrc',
            type: 'file',
            friendlyName: 'Feature Image Source',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          },
          {
            name: 'alt',
            type: 'text',
            friendlyName: 'Feature Image Alt Text',
          },
          {
            name: 'text',
            type: 'text',
            friendlyName: 'Feature Text',
          },
        ],
        defaultValue: [
        {
          imgSrc: '/assets/images/icon-news-1.png',
          alt: 'Single Origin',
          text: 'Single Origin, High-Altitude Grown (5,700+ ft)',
        },
        {
          imgSrc: '/assets/images/icon-news-2.png',
          alt: '3rd Party Tested',
          text: 'Molds, heavy metals plus 450+ toxins and pesticides" to "Molds, Heavy Metals Plus 450+ Toxins and Pesticides',
        },
        {
          imgSrc: '/assets/images/icon-news-3.png',
          alt: 'Low Acid',
          text: 'Stomach-Friendly Low Acid',
        },
        {
          imgSrc: '/assets/images/icon-news-4.png',
          alt: 'USDA Organic Certified',
          text: 'USDA Organic Certified',
        },
        {
          imgSrc: '/assets/images/icon-news-5.png',
          alt: 'Hand-Selected',
          text: 'Hand-Selected & Small Batch Roasted',
        },
        {
          imgSrc: '/assets/images/icon-news-6.png',
          alt: 'Shade Grown',
          text: 'Shade Grown & Spring Water Washed',
        },
        {
          imgSrc: '/assets/images/icon-news-7.png',
          alt: 'TRUSTPURE Process',
          text: 'TRUSTPURE process',
        },
      ]
      },
      {
        name: 'backgroundImageSrc',
        type: 'file',
        friendlyName: 'Background Image Source',
        allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
        defaultValue: '/assets/images/bg-mountain.png',
      },
      {
        name: 'backgroundImageAlt',
        type: 'text',
        friendlyName: 'Background Image Alt Text',
        defaultValue: 'Mountain background',
      },
      {
        name: 'backgroundHeading',
        type: 'richText',
        friendlyName: 'Background Heading',
        defaultValue: 'From Mountain to Morning Cup',
      },
      {
        name: 'backgroundDescription',
        type: 'richText',
        friendlyName: 'Background Description',
        default: `Our journey begins in the pristine mountains of Central America, where our coffee cherries are grown at elevations above 5,700 feet in rich, volcanic soil under a canopy of guava trees.
        <br><br>This painstaking process results in coffee that's:<br><br>
        • Naturally Low Acid (pH tested)<br>
        • Free from Harmful Mycotoxins<br>
        • Tested for 450+ different Pesticides, Chemicals, Heavy Metals, Glyphosate<br>
        • Easier on Your Stomach<br>
        • Clean, Smooth Taste<br>
        • Rich in Antioxidants<br>
        • Healthy coffee that actually tastes good`,
      },
    ],
  });
  </script>
  